import React, { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { StudentRecord } from '../types';
import ResultYearDropdown from '../components/ResultYearDropdown';
import {
  useGetResultStudentResultDetailsQuery,
} from '../services/api';

const GRADE_COLORS: Record<string, string> = {
  A1: 'bg-green-100 text-green-800',
  A2: 'bg-green-50 text-green-700',
  B1: 'bg-blue-100 text-blue-800',
  B2: 'bg-blue-50 text-blue-700',
  C1: 'bg-yellow-100 text-yellow-800',
  C2: 'bg-yellow-50 text-yellow-700',
  D1: 'bg-orange-100 text-orange-800',
  D2: 'bg-orange-50 text-orange-700',
  E: 'bg-red-100 text-red-800',
};

export default function StudentResultDetail() {
  const { regionCode, kvCode, rollNo } = useParams<{ regionCode: string; kvCode: string; rollNo: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classTab = (searchParams.get('class') as 'class10' | 'class12') || 'class10';
  const selectedYearParam = Number(searchParams.get('year') || '2025');
  const selectedYear = Number.isFinite(selectedYearParam) ? selectedYearParam : 2025;
  const grade = classTab === 'class10' ? 10 : 12;

  const handleYearChange = (year: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('year', String(year));
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  const sanitizeLabel = (value?: string | number | null): string => {
    const label = String(value ?? '').trim();
    if (!label || label.toLowerCase() === 'nan') {
      return '';
    }

    return label;
  };

  const toNumber = (value: unknown): number => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const {
    data: studentDetailsData,
    isFetching: isStudentsLoading,
    isError: isStudentsError,
  } = useGetResultStudentResultDetailsQuery(
    rollNo
      ? { grade, year: selectedYear, rollNo }
      : skipToken,
  );

  const student: StudentRecord | undefined = useMemo(
    () => {
      if (!studentDetailsData) {
        return undefined;
      }

      const mappedSubjects = (studentDetailsData.subjects || []).flatMap((subject) => {
        const subjectName = sanitizeLabel(subject.subjectName);
        const subjectCode = sanitizeLabel(subject.subjectCode);
        if (!subjectName || !subjectCode) {
          return [];
        }

        const theory = toNumber(subject.internalMarks);
        const practical = toNumber(subject.practicalMarks);
        const hasTotal = subject.theoryMarks !== null && subject.theoryMarks !== undefined;
        const total = hasTotal ? toNumber(subject.theoryMarks) : theory + practical;

        return [
          {
            code: subjectCode,
            name: subjectName,
            theory,
            practical,
            total,
            grade: sanitizeLabel(subject.grade).toUpperCase() || 'NA',
          },
        ];
      });

      const calculatedTotal = mappedSubjects.reduce((sum, subject) => sum + subject.total, 0);

      return {
        rollNo: sanitizeLabel(studentDetailsData.rollNo),
        name: sanitizeLabel(studentDetailsData.studentName) || `Student ${sanitizeLabel(studentDetailsData.rollNo)}`,
        fatherName: sanitizeLabel(studentDetailsData.fatherName) || 'N/A',
        motherName: sanitizeLabel(studentDetailsData.motherName) || 'N/A',
        dob: sanitizeLabel(studentDetailsData.dob) || 'N/A',
        gender: sanitizeLabel(studentDetailsData.gender) || 'N/A',
        category: sanitizeLabel(studentDetailsData.socialCategory) || 'N/A',
        region: sanitizeLabel(studentDetailsData.region) || sanitizeLabel(regionCode),
        kvCode: sanitizeLabel(studentDetailsData.kvCode),
        schoolName: sanitizeLabel(studentDetailsData.schoolName) || 'N/A',
        state: sanitizeLabel(studentDetailsData.state) || 'N/A',
        isPmShri: false,
        subjects: mappedSubjects,
        totalMarks: toNumber(studentDetailsData.totalMarks) || calculatedTotal,
        result: sanitizeLabel(studentDetailsData.result).toUpperCase() || 'UNKNOWN',
        session: String(selectedYear),
      };
    },
    [studentDetailsData, regionCode, selectedYear],
  );

  const classLabel = `Class ${grade}`;
  const isClass12 = grade === 12;
  const theme = isClass12
    ? {
        panelBorder: 'border-green-200',
        headerBg: 'bg-green-50',
        headerBorder: 'border-green-200',
        headerText: 'text-green-700',
        headerTitle: 'text-green-900',
        sectionBorder: 'border-green-100',
        tableHeader: 'bg-green-50 border-y border-green-200',
        rowBorder: 'border-green-100',
        footerBg: 'bg-green-50 font-semibold border-t border-green-200',
      }
    : {
        panelBorder: 'border-rose-200',
        headerBg: 'bg-rose-50',
        headerBorder: 'border-rose-200',
        headerText: 'text-rose-700',
        headerTitle: 'text-rose-900',
        sectionBorder: 'border-rose-100',
        tableHeader: 'bg-rose-50 border-y border-rose-200',
        rowBorder: 'border-rose-100',
        footerBg: 'bg-rose-50 font-semibold border-t border-rose-200',
      };

  if (!kvCode || !rollNo) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Invalid student URL.</p>
        <button onClick={() => navigate('..')} className="text-[#F36B21] text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  if (isStudentsLoading && !student) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-500">Loading student details...</div>
      </div>
    );
  }

  if (!student || isStudentsError) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Student not found.</p>
        <button onClick={() => navigate('..')} className="text-[#F36B21] text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  const totalMaxMarks = student.subjects.length * 100;

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Student Marksheet Preview</h1>
            <p className="text-sm text-gray-500">{classLabel} • Session {student.session}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ResultYearDropdown
            grade={grade}
            value={selectedYear}
            onChange={handleYearChange}
          />
          <span className={`text-sm font-bold px-4 py-2 rounded-full ${
            student.result === 'PASS' ? 'bg-green-100 text-green-700' :
            student.result === 'COMPTT' ? 'bg-orange-100 text-orange-700' :
            'bg-red-100 text-red-700'
          }`}>
            {student.result}
          </span>
        </div>
      </div>

      <div className={`bg-white border-2 rounded-2xl shadow-sm overflow-hidden ${theme.panelBorder}`}>
        <div className={`px-6 py-5 border-b text-center ${theme.headerBg} ${theme.headerBorder}`}>
          <p className={`text-sm font-semibold tracking-wide ${theme.headerText}`}>KENDRIYA VIDYALAYA SANGATHAN</p>
          <h2 className={`text-lg font-bold ${theme.headerTitle}`}>SECONDARY SCHOOL EXAMINATION, {student.session}</h2>
          <p className={`text-xs mt-1 ${theme.headerText}`}>STATEMENT OF MARKS</p>
        </div>

        <div className={`p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 border-b text-sm ${theme.sectionBorder}`}>
          <div className="space-y-2">
            <p><span className="text-gray-500">Name of Student:</span> <span className="font-semibold text-gray-900">{student.name}</span></p>
            <p><span className="text-gray-500">Roll No:</span> <span className="font-semibold text-gray-900">{student.rollNo}</span></p>
            <p><span className="text-gray-500">Gender:</span> <span className="font-semibold text-gray-900">{student.gender}</span></p>
            <p><span className="text-gray-500">Category:</span> <span className="font-semibold text-gray-900">{student.category}</span></p>
          </div>
          <div className="space-y-2">
            <p><span className="text-gray-500">School:</span> <span className="font-semibold text-gray-900">{student.schoolName}</span></p>
            <p><span className="text-gray-500">KV Code:</span> <span className="font-semibold text-gray-900">{student.kvCode}</span></p>
            <p><span className="text-gray-500">Region:</span> <span className="font-semibold text-gray-900">{student.region}</span></p>
            <p><span className="text-gray-500">State:</span> <span className="font-semibold text-gray-900">{student.state || 'N/A'}</span></p>
            <p><span className="text-gray-500">Class:</span> <span className="font-semibold text-gray-900">{classLabel}</span></p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={theme.tableHeader}>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase">Sub Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">Theory</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">Practical</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">Total / 100</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">Grade</th>
              </tr>
            </thead>
            <tbody>
              {student.subjects.map((sub, i) => (
                <tr key={sub.code + i} className={`border-b ${theme.rowBorder}`}>
                  <td className="px-4 py-3 text-sm text-gray-700 font-mono">{sub.code}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{sub.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-center">{sub.theory}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 text-center">{sub.practical}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-center">{sub.total}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${GRADE_COLORS[sub.grade] || 'bg-gray-100 text-gray-600'}`}>
                      {sub.grade}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={theme.footerBg}>
                <td className="px-4 py-3 text-sm text-gray-700" colSpan={2}>Grand Total</td>
                <td className="px-4 py-3" colSpan={2}></td>
                <td className="px-4 py-3 text-sm text-center text-gray-900">{student.totalMarks} / {totalMaxMarks}</td>
                <td className="px-4 py-3 text-center text-sm text-gray-900">{student.result}</td>
              </tr>
              <tr>
                <td colSpan={6} className="px-4 py-4 text-sm text-gray-500">
                  Note: Subject totals are shown as obtained marks out of 100 as per result data.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
